import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface Event {
  id: string;
  title: string;
  description?: string;
  event_date: string;
  end_date?: string;
  latitude: number;
  longitude: number;
  location_name: string;
  location_address?: string;
  event_type: 'public' | 'private';
  max_participants?: number;
  registration_deadline?: string;
  enable_chat: boolean;
  enable_checkin: boolean;
  checkin_radius?: number;
  status: 'active' | 'completed' | 'cancelled';
  user_id: string;
  created_at: string;
  cover_image_url?: string;
  participants_count?: number;
  user_registered?: boolean;
}

export const useEvents = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  const loadEvents = async () => {
    setLoading(true);
    try {
      // Carico gli eventi
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select('*')
        .eq('status', 'active')
        .gte('event_date', new Date().toISOString())
        .order('event_date', { ascending: true });

      if (eventsError) throw eventsError;

      // Carico i partecipanti per ogni evento
      const eventsWithParticipants = await Promise.all(
        (eventsData || []).map(async (event) => {
          const { data: participants, error: participantsError } = await supabase
            .from('event_participants')
            .select('user_id')
            .eq('event_id', event.id);

          if (participantsError) {
            console.error('Error loading participants for event:', event.id, participantsError);
          }

          return {
            ...event,
            event_type: event.event_type as 'public' | 'private',
            status: event.status as 'active' | 'completed' | 'cancelled',
            participants_count: participants?.length || 0,
            user_registered: participants?.some((p: any) => p.user_id === user?.id) || false
          };
        })
      );

      setEvents(eventsWithParticipants);
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Errore nel caricamento eventi');
    } finally {
      setLoading(false);
    }
  };

  const joinEvent = async (eventId: string) => {
    if (!user) return;
    
    try {
      const { data: existingParticipant } = await supabase
        .from('event_participants')
        .select('id')
        .eq('event_id', eventId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingParticipant) {
        toast.error('Sei già iscritto a questo evento');
        return;
      }

      const { error } = await supabase
        .from('event_participants')
        .insert([{ event_id: eventId, user_id: user.id }]);

      if (error) throw error;
      toast.success('Iscrizione completata!');
      loadEvents();
    } catch (error) {
      console.error('Error joining event:', error);
      toast.error('Errore nell\'iscrizione');
    }
  };

  const leaveEvent = async (eventId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('event_participants')
        .delete()
        .eq('event_id', eventId)
        .eq('user_id', user.id);

      if (error) throw error;
      toast.success('Iscrizione annullata');
      loadEvents();
    } catch (error) {
      console.error('Error leaving event:', error);
      toast.error('Errore nell\'annullamento iscrizione');
    }
  };

  const openInMaps = (event: Event) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${event.latitude},${event.longitude}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    loadEvents();
  }, []);

  return {
    events,
    loading,
    loadEvents,
    joinEvent,
    leaveEvent,
    openInMaps
  };
};